import { Button } from "@design-system/Inputs/Button";
import { FlexCol } from "@design-system/Layout/Flex";
import {
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    type RowSelectionState,
    Table,
    type TableOptions,
    useColumnPinning,
} from "@design-system/Table";
import { Text } from "@design-system/Typography/Text";
import {
    type KbDocumentType,
    type KnowledgeBaseItemStatus,
    KnowledgeBaseItemType,
} from "@generated/client/graphql";
import { useSetAtom } from "jotai";
import { type FC, useEffect, useMemo, useState } from "react";
import { isAddCustomItemModalOpenAtom } from "../../ctx";
import { useKnowledgeBase } from "../../data";
import { type KnowledgeBaseListItem } from "../../service";
import { ActionBar } from "../ActionBar";
import {
    pinnedLeftColumnsIds,
    pinnedRightColumnsIds,
    useColumns,
} from "./columns";
import { useKnowledgeBaseTableTranslations } from "./i18n";

export const KnowledgeBaseTable: FC<{
    searchString: string | undefined;
    selectedStatuses: (KnowledgeBaseItemStatus | undefined)[];
    selectedTypes: (KbDocumentType | undefined)[];
    selectedEntityIds: (string | undefined)[];
    activeType: KnowledgeBaseItemType;
}> = ({
    searchString,
    selectedStatuses,
    selectedTypes,
    selectedEntityIds,
    activeType,
}) => {
    const { knowledgeBaseItems, isPending } = useKnowledgeBase({
        type: activeType,
    });

    const { columnPinning } = useColumnPinning({
        initialColumnPinningState: {
            left: pinnedLeftColumnsIds,
            right: pinnedRightColumnsIds,
        },
    });

    const filteredKnowledgeBaseItems = useMemo(() => {
        const filterByStatus = !!selectedStatuses.length;

        const filterByType = !!selectedTypes.length;

        const filterByEntity = !!selectedEntityIds.length;

        const filterBySearch = !!searchString;

        return knowledgeBaseItems.filter((item) => {
            if (
                filterByStatus &&
                !selectedStatuses.includes(item?.status ?? undefined)
            ) {
                return false;
            }

            if (
                filterByType &&
                !selectedTypes.includes(item?.document?.documentType)
            ) {
                return false;
            }

            if (
                filterByEntity &&
                !selectedEntityIds.includes(item?.entity?.id)
            ) {
                return false;
            }

            if (
                filterBySearch &&
                !item.name
                    ?.toLowerCase()
                    .includes(searchString.toLowerCase()) &&
                !item.customItem?.description
                    ?.toLowerCase()
                    .includes(searchString.toLowerCase()) &&
                !item.customItem?.value
                    ?.toLowerCase()
                    .includes(searchString.toLowerCase())
            ) {
                return false;
            }

            return true;
        });
    }, [
        knowledgeBaseItems,
        selectedStatuses,
        selectedTypes,
        selectedEntityIds,
        searchString,
    ]);

    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });

    const options: Partial<TableOptions<KnowledgeBaseListItem>> = useMemo(
        () => ({
            getPaginationRowModel: getPaginationRowModel(),
            getSortedRowModel: getSortedRowModel(),
            getFilteredRowModel: getFilteredRowModel(),
            onRowSelectionChange: setRowSelection,
            getRowId: (row) => row.id,
            state: {
                rowSelection,
            },
            autoResetPageIndex: false,
        }),
        [rowSelection, setRowSelection],
    );

    const unsetSelection = () => {
        setRowSelection({});
    };

    useEffect(() => {
        setPagination({ ...pagination, pageIndex: 0 });
    }, [
        setPagination,
        searchString,
        selectedStatuses,
        selectedTypes,
        selectedEntityIds,
    ]);

    useEffect(() => {
        setRowSelection({});
    }, [activeType]);

    const isFilterActive =
        !!searchString?.length ||
        selectedStatuses.length > 0 ||
        selectedTypes.length > 0 ||
        selectedEntityIds.length > 0;

    const emptyBody = () => EmptyState({ activeType, isFilterActive });

    const selectableItemIds = useMemo(() => {
        return filteredKnowledgeBaseItems
            .filter((item) => item.businessLogicAllowsPoweringAI)
            .map((item) => item.id);
    }, [filteredKnowledgeBaseItems]);

    const selectedItemIds = useMemo(() => {
        return selectableItemIds.filter((id) => rowSelection[id]);
    }, [rowSelection, selectableItemIds]);

    const columns = useColumns(activeType, !!selectableItemIds.length);

    return (
        <FlexCol gap="3" px="4" py="3">
            <Table
                columns={columns}
                data={filteredKnowledgeBaseItems}
                EmptyBody={emptyBody}
                pagination={{
                    state: pagination,
                    onPaginationChange: setPagination,
                    manualPagination: false,
                }}
                loading={isPending}
                options={options}
                variant="grid"
                columnPinning={columnPinning}
            />
            <ActionBar
                unset={unsetSelection}
                selectedRowIds={selectedItemIds}
            />
        </FlexCol>
    );
};

const EmptyState: FC<{
    activeType: KnowledgeBaseItemType;
    isFilterActive: boolean;
}> = ({ activeType, isFilterActive }) => {
    const { t, keys } = useKnowledgeBaseTableTranslations();

    const setOpen = useSetAtom(isAddCustomItemModalOpenAtom);

    const handleClick = () => {
        setOpen(true);
    };

    return (
        <FlexCol py="7" alignItems="center" className="bg-neutral-100" h="full">
            <FlexCol gap="1" alignItems="center">
                {activeType === KnowledgeBaseItemType.CustomItem &&
                !isFilterActive ? (
                    <FlexCol gap="0" alignItems="center">
                        <Text variant="body-bold" align="center">
                            {t(keys.no_custom_items)}
                        </Text>
                        <Text
                            variant="body-sm"
                            align="center"
                            color="secondary"
                        >
                            {t(keys.add_custom_item_instructions)}
                        </Text>
                        <Button
                            variant="outlined"
                            size="sm"
                            onClick={handleClick}
                            className="mt-3"
                        >
                            {t(keys.add_custom_item)}
                            <Button.Icon
                                name="plus"
                                size="sm"
                                color="primary"
                            />
                        </Button>
                    </FlexCol>
                ) : isFilterActive ? (
                    <Text variant="body-bold">{t(keys.no_results_found)}</Text>
                ) : (
                    <FlexCol gap="0" alignItems="center">
                        <Text variant="body-bold">
                            {t(keys.empty_state_title, {
                                context: activeType,
                            })}
                        </Text>
                        <Text variant="body-sm" color="secondary">
                            {t(keys.empty_state_description, {
                                context: activeType,
                            })}
                        </Text>
                    </FlexCol>
                )}
            </FlexCol>
        </FlexCol>
    );
};
